import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {
  FiCheck,
  FiFileText,
  FiPlus,
  FiTrendingUp,
  FiUsers,
} from "react-icons/fi";
import Header from "../components/header/Header";
import ProgressBar from "../components/progressBar/ProgressBar";
import GuideSection from "../components/guide/GuideSection";
import { managementGuide } from "../components/guide/guideContent";
import {
  CREATION_STEPS,
  PeriodPicker,
  PriceBlock,
  SummaryBar,
  showError,
} from "../components/options/OptionParts";
import { addToCart } from "../redux/actions/CartActions";
import { addToServiceList } from "../redux/actions/ServiceListActions";
import {
  OdooRequest,
  updateOdooRequest,
} from "../redux/actions/RequestActions";
import pricing from "../assets/data/pricing.json";
import serviceCode from "../assets/data/serviceCode.json";
import { formatPrice } from "../utils/formatPrice";
import "./styles/options.css";

// Modules of the Gestion 360 ERP, billed per module
const MODULES = [
  {
    key: "management",
    icon: FiFileText,
    title: "Facturation",
    summary: "Devis, factures, règlements et suivi de trésorerie.",
    prices: pricing.platformGest,
  },
  {
    key: "grh",
    icon: FiUsers,
    title: "Ressources humaines",
    summary: "Dossiers des employés, contrats, présences et congés.",
    prices: pricing.platformGRH,
  },
  {
    key: "sales",
    icon: FiTrendingUp,
    title: "Relation client (CRM)",
    summary: "Suivi des clients, relances, pipeline commercial et activités.",
    prices: pricing.platformSale,
  },
];

function PlatformGest() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cart = useSelector((state) => state.cart);
  const { user } = useSelector((state) => state.authUser);
  const { company } = useSelector((state) => state.companies);
  const { odooRequest } = useSelector((state) => state.request);

  // Previous choices are restored when the user comes back to this step
  const [periodicity, setPeriodicity] = useState(
    cart?.platformGest?.periodicity || "mensuel",
  );
  const [selectedModules, setSelectedModules] = useState(
    cart?.platformGest?.modules || [],
  );
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const toggleModule = (key) =>
    setSelectedModules((prev) =>
      prev.includes(key) ? prev.filter((m) => m !== key) : [...prev, key],
    );

  // Monthly amount, the yearly plan is billed as 12 months
  const monthlyTotal = MODULES.filter((mod) =>
    selectedModules.includes(mod.key),
  ).reduce((sum, mod) => sum + mod.prices[periodicity], 0);
  const billing = periodicity === "annuel" ? "yearly" : "monthly";

  const handleContinue = async () => {
    if (selectedModules.length === 0) return;
    setSubmitting(true);

    await dispatch(
      odooRequest?.reference
        ? updateOdooRequest(
            user.token,
            odooRequest.reference,
            { billing, modules: selectedModules },
            handleSaved,
          )
        : OdooRequest(
            user.token,
            {
              company: company?._id || cart?.company?._id,
              billing,
              modules: selectedModules,
            },
            handleSaved,
          ),
    );
  };

  const handleSaved = async ({ success, message }) => {
    setSubmitting(false);

    if (!success) {
      showError(Swal, message);
      return;
    }

    await dispatch(
      addToCart({
        platformGest: {
          periodicity,
          billing,
          modules: selectedModules,
          amount: monthlyTotal,
        },
      }),
    );
    await dispatch(
      addToServiceList({
        code: serviceCode.platformGest,
        billing,
        modules: selectedModules,
      }),
    );

    navigate("/cart");
  };

  const count = selectedModules.length;

  return (
    <div className="options-page">
      <Header />
      <ProgressBar steps={CREATION_STEPS} currentStep="Services" />

      <div className="container options-container">
        <header className="options-header">
          <h1 className="options-title">
            Composez votre <span>Gestion 360°</span>
          </h1>
          <p className="options-lead">
            Un ERP en ligne pour piloter votre entreprise. Choisissez les
            modules dont vous avez besoin : vous ne payez que ceux que vous
            activez.
          </p>
        </header>

        <div className="erp-period">
          <PeriodPicker value={periodicity} onChange={setPeriodicity} />
        </div>

        <div className="options-grid">
          {MODULES.map((mod) => {
            const selected = selectedModules.includes(mod.key);
            const Icon = mod.icon;

            return (
              <article
                key={mod.key}
                className={`option-card ${selected ? "is-selected" : ""}`}
              >
                <div className="option-card-top">
                  <span className="option-icon" aria-hidden="true">
                    <Icon />
                  </span>
                  <button
                    type="button"
                    className="option-toggle"
                    aria-pressed={selected}
                    onClick={() => toggleModule(mod.key)}
                  >
                    {selected ? (
                      <>
                        <FiCheck aria-hidden="true" /> Ajouté
                      </>
                    ) : (
                      <>
                        <FiPlus aria-hidden="true" /> Ajouter
                      </>
                    )}
                  </button>
                </div>

                <h2 className="option-title">{mod.title}</h2>
                <p className="option-summary">{mod.summary}</p>

                <div className="option-card-pricing">
                  <PriceBlock prices={mod.prices} periodicity={periodicity} />
                </div>
              </article>
            );
          })}
        </div>
      </div>

      <GuideSection {...managementGuide} />

      <SummaryBar
        label={
          count === 0
            ? "Choisissez au moins un module"
            : `${count} module${count > 1 ? "s" : ""} · ${
                periodicity === "annuel"
                  ? "formule annuelle"
                  : "formule mensuelle"
              }`
        }
        total={
          count === 0
            ? "—"
            : periodicity === "annuel"
              ? `${formatPrice(monthlyTotal * 12)} / an`
              : `${formatPrice(monthlyTotal)} / mois`
        }
        buttonLabel={submitting ? "Enregistrement…" : "Continuer"}
        disabled={submitting || count === 0}
        onContinue={handleContinue}
      />
    </div>
  );
}

export default PlatformGest;
